'use client'

import {
  useGetCompanySearchQuery,
  useGetNewsSearchQuery,
  useGetProductSearchQuery,
} from '@/app/globalRedux/api/searchSlice'
import {
  setCompanyOrder,
  setCompanySelectedOption,
} from '@/app/globalRedux/filter/companyFilter'
import {
  setNewsOrder,
  setNewsSortSelectedOption,
} from '@/app/globalRedux/filter/newsFilter'
import {
  setProductOrder,
  setProductSelectedSortOption,
} from '@/app/globalRedux/filter/productFilter'
import { RootState } from '@/app/globalRedux/store'
import useLocalizedRouter from '@/app/hooks/useLocalizedRouter'
import { useTranslation } from '@/app/i18n/client'
import { CompanyResponse } from '@/app/types/companySearchType'
import { NewsSearchResponse } from '@/app/types/newsSearchType'
import { ProductSearchResponse } from '@/app/types/productSearchType'
import SearchResultSort from '@/components/SearchResultSelect'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { openModal } from '@/lib/filterMobileModalHandler'
import { convertNaNToOne } from '@/lib/formatUtils'
import { formatNumber } from '@/services/generalDataServices'
import Image from 'next/image'
import { useParams, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Filters from '../../Classses/Filters/Filters'
import CompanyFilter from './(Filters)/CompanyFilters/CompanyFilter'
import NewsFilter from './(Filters)/NewsFilter'
import ProductFilter from './(Filters)/ProductFilter/ProductFilter'

const SearchResults = () => {
  const params = useParams()
  const searchText = useSelector(
    (state: RootState) => state.searchData.searchTerm
  )
  const companyFilter = useSelector((state: RootState) => state.companyFilter)
  const newsFilter = useSelector((state: RootState) => state.newsFilter)
  const productsFilter = useSelector((state: RootState) => state.productFilter)
  const commonFilter = useSelector((state: RootState) => state.commonFilter)

  const { data: companyDataProductBased } = useGetCompanySearchQuery<{
    data: CompanyResponse
    isFetching: boolean
  }>(
    {
      search_on: 'product',
      companyFilter: companyFilter,
      commonFilter: commonFilter,
      productFilter: productsFilter,
      lang: String(params?.lng),
      pageNo: Number(params?.companyPageNo?.[0] || 1),
    },
    {
      skip: companyFilter.isRouteUpdating,
    }
  )

  const { data: companyDataNameBased } = useGetCompanySearchQuery<{
    data: CompanyResponse
    isFetching: boolean
  }>(
    {
      search_on: 'company_name',
      companyFilter: companyFilter,
      commonFilter: commonFilter,
      productFilter: productsFilter,
      lang: String(params?.lng),
      pageNo: convertNaNToOne(Number(params?.companyPageNo?.[0] || 1)),
    },
    {
      skip: companyFilter.isRouteUpdating,
    }
  )

  const { data: productData } = useGetProductSearchQuery<{
    data: ProductSearchResponse
    isFetching: boolean
    isError: boolean
    isLoading: boolean
    isSuccess: boolean
  }>({
    productsFilter: productsFilter,
    commonFilter: commonFilter,
    pageNo: convertNaNToOne(Number(params?.productPageNO?.[0]) || 1),
  })

  const { data: newsData } = useGetNewsSearchQuery<{
    data: NewsSearchResponse
    isFetching: boolean
    isError: boolean
    isLoading: boolean
    isSuccess: boolean
  }>(
    {
      newsFilter: newsFilter,
      commonFilter: commonFilter,
      pageNO: convertNaNToOne(Number(params?.pageNO?.[0] ?? 1)),
    },
    {
      skip: newsFilter.isNewsRouteLoading,
    }
  )
  const modalHandler: {
    id: string
    component: JSX.Element
    tab: string
  }[] = [
    {
      id: 'company-modal',
      component: <CompanyFilter search="product" />,
      tab: 'PRODUCT_COMPANY_TAB',
    },
    {
      id: 'company-modal',
      component: <CompanyFilter search="company_name" />,
      tab: 'COMPANY_TAB',
    },
    {
      id: 'product-modal',
      component: <ProductFilter />,
      tab: 'PRODUCT_TAB',
    },
    {
      id: 'news-modal',
      component: <NewsFilter />,
      tab: 'NEWS_TAB',
    },
  ]

  const { t } = useTranslation()
  const pathName = usePathname()
  const pushWithLocale = useLocalizedRouter()
  const activeTab = Filters.getTab(pathName)
  const [currentMobileModal, setCurrentMobileModal] = useState<{
    id: string
    component: JSX.Element
    tab: string
  }>(modalHandler[0])

  useEffect(() => {
    const modal =
      modalHandler.find((item) => item.tab === Filters.getTab(pathName)) ??
      modalHandler[0]
    setCurrentMobileModal(modal)
  }, [pathName])

  const tabs = [
    {
      name: 'PRODUCT_COMPANY_TAB',
      count: companyDataProductBased?.count,
      displayText: 'Products',
    },
    {
      name: 'COMPANY_TAB',
      count: companyDataNameBased?.count,
      displayText: 'Companies',
    },
    { name: 'PRODUCT_TAB', count: productData?.count, displayText: 'Offers' },
    { name: 'NEWS_TAB', count: newsData?.count, displayText: 'News' },
  ]
  const switchTabs = (tab: string) => {
    pushWithLocale(
      `/search/${Filters.getKeysByValue(tab)}?searchTerm=${searchText}`
    )
  }

  return (
    <div className="pb-5">
      <div className="flex flex-col justify-between align-center mobile:mt-4">
        <div className="flex text-grey_forty items-center gap-3 mobile-width mobile:justify-around mobile:overflow-x-auto mobile:pr-3">
          {tabs.map((tab) =>
            companyFilter?.sector === 'Retail Chains' &&
            ['PRODUCT_TAB'].includes(tab.name) ? (
              ''
            ) : (
              <Button
                key={tab.name}
                className={`py-0 px-1 pb-2 ${activeTab === tab.name ? 'text-secondary font-ns_bold rounded-none border-b-secondary border-b-2 ' : 'font-ns_regular'}`}
                variant="plain"
                size="rounded"
                onClick={() => {
                  switchTabs(tab.name)
                }}
              >
                {t(tab.displayText)}
                {tab?.count ? ` (${formatNumber(tab?.count)})` : null}
              </Button>
            )
          )}
        </div>
        <Separator />
        <div className="flex items-center justify-end mt-2 mobile-width">
          <div className="mobile:hidden">
            <SortSearchResults />
          </div>
          <div className="ml-auto mobile:block hidden">
            <Image
              onClick={() => openModal(currentMobileModal.id)}
              loading="lazy"
              width={30}
              height={30}
              src="/assets/Icons/mobile-filter-icon.svg"
              alt="filter-icon"
              className="border-[#ddd] border-solid bg-[#F4F4F4] px-[5px] py-[5px] rounded"
            />
            <div
              id={currentMobileModal.id}
              className="modal-hidden fixed top-0 left-0 z-50 w-full h-full mobile:flex items-center justify-center bg-gray-900 bg-opacity-50 overflow-y-hidden"
            >
              <div className="bg-white rounded-lg overflow-y-auto shadow-xl transform transition-all sm:max-w-lg sm:w-full mobile:h-[80vh] mobile-width mobile:py-[10px] mobile:px-[10px] p-5">
                {currentMobileModal.component}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchResults

const SortSearchResults = () => {
  const pathName = usePathname()
  const dispatch = useDispatch()
  const { order: companyOrder, companySelectedSortOption } = useSelector(
    (state: RootState) => state.companyFilter
  )
  const { order: productOrder, productSelectedSortOption } = useSelector(
    (state: RootState) => state.productFilter
  )
  const { order: newsOrder, selectedNewsSortOption } = useSelector(
    (state: RootState) => state.newsFilter
  )

  if (Filters.getTab(pathName).includes('COMPANY_TAB')) {
    return (
      <SearchResultSort
        options="companySort"
        selectedOption={companySelectedSortOption}
        sortByFunction={(value) => {
          dispatch(setCompanySelectedOption(value))
        }}
        order={companyOrder}
        orderByFunction={(value) => {
          dispatch(setCompanyOrder(value))
        }}
      />
    )
  }

  if (Filters.getTab(pathName) === 'PRODUCT_TAB') {
    return (
      <SearchResultSort
        options="productSort"
        selectedOption={productSelectedSortOption}
        sortByFunction={(value) => {
          dispatch(setProductSelectedSortOption(value))
        }}
        order={productOrder}
        orderByFunction={(value) => {
          dispatch(setProductOrder(value))
        }}
      />
    )
  }
  if (Filters.getTab(pathName) === 'NEWS_TAB') {
    return (
      <SearchResultSort
        options="NewsSort"
        selectedOption={selectedNewsSortOption}
        sortByFunction={(value) => {
          dispatch(setNewsSortSelectedOption(value))
        }}
        order={newsOrder}
        orderByFunction={(value) => {
          dispatch(setNewsOrder(value))
        }}
      />
    )
  }
}
